//************************************************************************ /
// EVENT CTA CARD
// White card suggesting an action to the user and providing a link to do it.
// Initially themed off the cta-card in the communities package.
//************************************************************************ /

import React from "react";
import {
  Card,
  Button,
  Group,
  Typography,
} from "@dreambigger/design-system/src/components";
import { RightOutlined } from "@ant-design/icons";
import { getIcon } from "@dreambigger/design-system/src/utils";
import { getDateFnsTimeZone } from "@dreambigger/shared/src/utils";
import { Event as EventType, Community } from "@dreambigger/shared/src/types";
import { formatInTimeZone } from "date-fns-tz";
import { isAfter } from "date-fns";
import InspireAnalytics, {
  InspireAnalyticEvents,
} from "@dreambigger/shared/src/utils/inspire-analytics-helper";
import { useMixpanel, useGA, useSegment } from "@dreambigger/shared/src/hooks";

const { Title, Text } = Typography;

type DescriptionProps = {
  event: EventType;
  community: Community;
};

const DescriptionCard = ({ event, community }: DescriptionProps) => {
  // Load Analytics
  const mixpanel = useMixpanel();
  const segment = useSegment();
  const googleAnalytics = useGA();

  const handleShareClick = () => {
    window.location.pathname = window.location.pathname.replace(
      "detail",
      "share"
    );
  };

  const handleRSVPClick = () => {
    // Track analytics of event click
    googleAnalytics.event({
      category: "External Event - RSVP Flow - Start",
      action: "Select",
      label: "RSVP",
      value: 1,
    });
    InspireAnalytics.track({
      eventName: InspireAnalyticEvents.RSVP_START,
      properties: {
        "CTA Type": "Attend This Project",
        "RSVP Page Type": "Standard",
      },
      mixpanel,
      segment,
    });

    // Take user to RSVP page
    window.location.pathname = window.location.pathname.replace(
      "detail",
      "rsvp"
    );
  };

  // Return if no timeZone is entered - All times will be wrong
  if (!event.timeZone) {
    return <></>;
  }
  // Convert DB timeZone into a format readable by dateFns. Return if conversion fails.
  const timezone = getDateFnsTimeZone(event.timeZone);
  if (!timezone) {
    return <></>;
  }
  // Define start and end times.
  const startAt = event.startAt || null;
  const endAt = (startAt && event.endAt) || null;

  // Store abbreviated timezone (PST, EDT, etc)
  const AbbrTimezone = startAt
    ? formatInTimeZone(startAt, timezone, "zzz")
    : "";

  // Store formatted dates and times for single-day events
  // --- For a full list of all date-fns parsing options, see https://date-fns.org/v2.28.0/docs/parse
  // TODO: Add this functionality to get-formatted-times.ts
  const startDate = startAt
    ? formatInTimeZone(startAt, timezone, "EEEE, MMM dd, yyyy") // Example - Monday, Mar 03, 2022
    : "Coming Soon!";
  const startTime = startAt
    ? formatInTimeZone(startAt, timezone, "h:mm a") // Example - 5:00 PM
    : "Check back later for more information";
  const endDate = endAt // Ex:
    ? formatInTimeZone(endAt, timezone, "EEEE, MMM dd, yyyy") // Example - Tuesday, Mar 04, 2022
    : null;
  const endTime = endAt ? formatInTimeZone(endAt, timezone, "h:mm a") : null; // Example - 6:00 PM

  // Store formatted dates and times for multi-day events. See above for documentation.
  const multiDayStartDate = startAt
    ? formatInTimeZone(startAt, timezone, "MMM do, yyyy") // Example - Feb 28th, 2022
    : "Coming Soon!";
  const multiDayStartTime = startAt
    ? formatInTimeZone(startAt, timezone, "EEE. h:mm a") // Example - Tue. 5:00 PM
    : "Check back later for more information";
  const multiDayEndDate = endAt
    ? formatInTimeZone(endAt, timezone, "MMM do, yyyy") // Example - Mar 1st, 2022
    : null;
  const multiDayEndTime = endAt
    ? formatInTimeZone(endAt, timezone, "iii. h:mm a") // Example - 6:00 PM
    : null;

  // Check if event is in the past
  const inThePast = isAfter(new Date(), new Date(startDate));
  return (
    <Card>
      <Group spacing={5}>
        {/* Title */}
        <Group spacing={1}>
          {event.name && (
            <Title level={3} className="lh-3">
              {event.name}
            </Title>
          )}
          {/* Hosted By */}
          {event.host && (
            <Text type="secondary" className="sm_f-4">
              Hosted by: {event.host}
            </Text>
          )}
        </Group>
        {/* Details */}
        <Group spacing={4}>
          {/* Date and Time */}
          {event.startAt && (
            <Group xs="horizontal" spacing={3}>
              <div className="w-6 gray-6">{getIcon("calendar", 2)}</div>
              {/* ---------------------------- Formatted Date/Time for Multi-Day Events ------------------------------ */}
              {/*     An event is "Multi-Day" if it has a defined endDate that is different from the startDate */}
              {endDate && startDate !== endDate ? (
                <Group>
                  <Text className="primaryText fwb f-4">
                    {/* IE: Feb 28th to Mar 1st */}
                    {multiDayStartDate} to {multiDayEndDate}{" "}
                  </Text>
                  <Text type="secondary">
                    {/* Set the time as "All Day" or a range (IE: Tue. 5:00 PM - Wed. 5:00 PM EST) */}
                    {event.allDay
                      ? "All Day"
                      : `${multiDayStartTime} 
                    ${
                      multiDayStartTime &&
                      multiDayEndTime &&
                      multiDayStartTime !== multiDayEndTime
                        ? `- ${multiDayEndTime}`
                        : ""
                    } 
                    ${" "} ${AbbrTimezone}`}
                  </Text>
                </Group>
              ) : (
                // ---------------------------- Formatted Date/Time for Single-Day Events ------------------------------ */}
                //                  An event is "Single-Day" if it is not a "Multi-Day" --- See ternary above
                <Group>
                  {/* IE: Friday, March 11 */}
                  <Text className="fwb f-4">{startDate} </Text>
                  <Text type="secondary">
                    {/* Set the time as "All Day" or a range (IE: 5:00 PM - 6:00 PM EST) */}
                    {event.allDay
                      ? "All Day"
                      : `${startTime} 
                      ${endTime && startTime !== endTime ? `- ${endTime}` : ""} 
                      ${" "} ${AbbrTimezone}`}
                  </Text>
                </Group>
              )}
            </Group>
          )}

          {/* Event Location */}
          {event.location.name && (
            <Group xs="horizontal" spacing={3}>
              <div className="w-6 gray-6">{getIcon("map", 2)}</div>
              <Text className="f-4">{event.location.name}</Text>
            </Group>
          )}
        </Group>
        {/* If event is in the past, direct the user to share a story about it. Otherwise, let them RSVP. */}
        {inThePast ? (
          <Button
            className={`primary b-primary ttu primaryBkdWhiteTxt h-7 w-100 md_w-auto lg_w-100 pointer`}
            style={{ border: "solid 2px", transition: "0.1s", minWidth: 270 }}
            onClick={handleShareClick}
          >
            <RightOutlined className="vh" /> <span>Share Story</span>{" "}
            <RightOutlined />
          </Button>
        ) : (
          <Button
            className={`primary b-primary ttu primaryBkdWhiteTxt h-7 w-100 md_w-auto lg_w-100 pointer`}
            style={{ border: "solid 2px", transition: "0.1s", minWidth: 270 }}
            onClick={handleRSVPClick}
          >
            <RightOutlined className="vh" />{" "}
            <span>
              Attend this {community.meta?.marketing?.keywordUpper || "Event"}
            </span>{" "}
            <RightOutlined />
          </Button>
        )}
      </Group>
    </Card>
  );
};

export default DescriptionCard;
